<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { decode } from 'blurhash'

interface ViewerImage {
  ImageId: number
  Key: string
  Title: string | null
  Description: string | null
  BlurHash: string | null
  ExifData: string | null
  CreatedAt: string
}

const props = defineProps<{
  images: ViewerImage[]
  initialIndex: number
  visible: boolean
}>()

const emit = defineEmits<{
  close: [currentIndex: number]
}>()

// 当前索引
const currentIndex = ref(props.initialIndex)

// 图片加载状态
const imageLoaded = ref(false)

// 缩放与旋转
const scale = ref(1)
const rotation = ref(0)

// 信息面板显示
const showInfo = ref(false)

// BlurHash canvas ref
const canvasRef = ref<HTMLCanvasElement | null>(null)

// 拖拽相关状态
const isDragging = ref(false)
const startX = ref(0)
const startY = ref(0)
const currentTranslateX = ref(0)
const currentTranslateY = ref(0)
const lastTranslateX = ref(0)
const lastTranslateY = ref(0)

// 双指缩放相关状态
const isPinching = ref(false)
const initialPinchDistance = ref(0)
const initialPinchScale = ref(1)

// 动画控制
const isAnimating = ref(false)

// 当前图片
const currentImage = computed(() => props.images[currentIndex.value])

// 总数
const total = computed(() => props.images.length)

// 判断是否为绝对路径（http/https 开头）
const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url)

// 获取原图 URL
const getFullImageUrl = (key: string) => {
  if (isAbsoluteUrl(key)) {
    return key // 绝对路径直接返回
  }
  return `https://image.lixiang.page/${key}`
}

// 获取缩略图 URL（用于预加载）
const getThumbUrl = (key: string) => {
  if (isAbsoluteUrl(key)) {
    return key // 绝对路径直接返回
  }
  return `https://image.lixiang.page/cdn-cgi/image/format=auto,quality=40,width=800/${key}`
}

// 解析 EXIF 数据
const parsedExif = computed(() => {
  if (!currentImage.value?.ExifData) return null
  try {
    const data = JSON.parse(currentImage.value.ExifData)
    return {
      // 设备信息
      make: data.make || data.Make || null,
      model: data.model || data.Model || null,
      // 拍摄参数
      exposureTime: data.exposureTime || data.ExposureTime || data.shutterSpeed || null,
      fNumber: data.fNumber || data.FNumber || data.aperture || null,
      iso: data.iso || data.ISO || data.ISOSpeedRatings || null,
      focalLength: data.focalLength || data.FocalLength || null,
      // 其他
      lens: data.lens || data.LensModel || null,
      software: data.software || data.Software || null,
      dateTime: data.dateTime || data.DateTime || data.DateTimeOriginal || null,
    }
  } catch {
    return null
  }
})

// 格式化设备名称 (制造商 + 型号)
const deviceName = computed(() => {
  if (!parsedExif.value) return null
  const { make, model } = parsedExif.value
  if (!make && !model) return null
  if (make && model) {
    // 如果 model 已经包含 make，则只显示 model
    if (model.toLowerCase().includes(make.toLowerCase())) {
      return model
    }
    return `${make} ${model}`
  }
  return model || make
})

// 格式化曝光时间 (如 1/100)
const formatExposureTime = (value: number | string | null) => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (value >= 1) return `${value}s`
  // 转换为分数形式
  const denominator = Math.round(1 / value)
  return `1/${denominator}s`
}

// 格式化光圈值 (如 f/1.8)
const formatAperture = (value: number | string | null) => {
  if (!value) return null
  if (typeof value === 'string') return value.startsWith('f') ? value : `f/${value}`
  return `f/${value}`
}

// 格式化焦距 (如 120mm)
const formatFocalLength = (value: number | string | null) => {
  if (!value) return null
  if (typeof value === 'string') return value.includes('mm') ? value : `${value}mm`
  return `${Math.round(value)}mm`
}

// 格式化日期
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 渲染 BlurHash
const renderBlurHash = () => {
  if (!currentImage.value?.BlurHash || !canvasRef.value) return

  try {
    const pixels = decode(currentImage.value.BlurHash, 32, 32)
    const ctx = canvasRef.value.getContext('2d')
    if (ctx) {
      const imageData = ctx.createImageData(32, 32)
      imageData.data.set(pixels)
      ctx.putImageData(imageData, 0, 0)
    }
  } catch (e) {
    console.warn('BlurHash decode failed:', e)
  }
}

// 图片加载完成
const handleImageLoad = () => {
  imageLoaded.value = true
}

// 切换图片时重置状态
const resetViewState = () => {
  imageLoaded.value = false
  scale.value = 1
  rotation.value = 0
  showInfo.value = false
  currentTranslateX.value = 0
  currentTranslateY.value = 0
  lastTranslateX.value = 0
  lastTranslateY.value = 0
  nextTick(() => {
    renderBlurHash()
  })
}

// 上一张
const prev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
    resetViewState()
  }
}

// 下一张
const next = () => {
  if (currentIndex.value < total.value - 1) {
    currentIndex.value++
    resetViewState()
  }
}

// 关闭 Viewer
const close = () => {
  emit('close', currentIndex.value)
}

// 放大
const zoomIn = () => {
  scale.value = Math.min(scale.value + 0.5, 4)
}

// 缩小
const zoomOut = () => {
  scale.value = Math.max(scale.value - 0.5, 0.5)
  if (scale.value <= 1) {
    scale.value = 1
    currentTranslateX.value = 0
    currentTranslateY.value = 0
    lastTranslateX.value = 0
    lastTranslateY.value = 0
  }
}

// 重置缩放
const resetZoom = () => {
  scale.value = 1
  rotation.value = 0
  currentTranslateX.value = 0
  currentTranslateY.value = 0
  lastTranslateX.value = 0
  lastTranslateY.value = 0
}

// 旋转
const rotate = () => {
  rotation.value = (rotation.value + 90) % 360
}

// 双击放大/还原
let lastTapTime = 0
let lastTapX = 0
let lastTapY = 0
// 单指触摸手势：用于区分 tap/double-tap 与 drag
let touchStartTime = 0
let touchStartClientX = 0
let touchStartClientY = 0
let touchMoved = false

// 计算两点之间的距离
const getDistance = (touch1: Touch, touch2: Touch) => {
  const dx = touch1.clientX - touch2.clientX
  const dy = touch1.clientY - touch2.clientY
  return Math.sqrt(dx * dx + dy * dy)
}

// 计算两点的中心点
const getCenter = (touch1: Touch, touch2: Touch) => {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2
  }
}

// 以指定点为中心进行缩放
const zoomAtPoint = (newScale: number, centerX: number, centerY: number, oldScale: number) => {
  // 限制缩放范围 (0.5-4倍)
  newScale = Math.max(0.5, Math.min(4, newScale))

  // 缩放回到 1x 时，强制居中，避免残留位移
  if (newScale <= 1) {
    scale.value = newScale
    currentTranslateX.value = 0
    currentTranslateY.value = 0
    lastTranslateX.value = 0
    lastTranslateY.value = 0
    return
  }

  if (newScale === oldScale || oldScale <= 0) return

  // 注意：当前 transform 顺序为 translate -> rotate -> scale（实际应用顺序为 scale->rotate->translate）
  // translate 是“屏幕空间”位移，因此缩放时不应对 translate 乘以 scaleChange。
  // 使用几何反解：保持点击点在屏幕上的位置不变，求新的 translate。
  const screenCenterX = window.innerWidth / 2
  const screenCenterY = window.innerHeight / 2

  const rad = (rotation.value * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  // 屏幕坐标系下：点击点相对于“图片中心”的向量（已包含当前 translate）
  const px = centerX - screenCenterX - currentTranslateX.value
  const py = centerY - screenCenterY - currentTranslateY.value

  // 反旋转，把向量还原到图片局部坐标系
  const localX = (px * cos + py * sin) / oldScale
  const localY = (-px * sin + py * cos) / oldScale

  // 再按 newScale 旋转回屏幕坐标，得到缩放后该局部点在屏幕中的向量
  const scaledX = (localX * newScale) * cos + (localY * newScale) * -sin
  const scaledY = (localX * newScale) * sin + (localY * newScale) * cos

  currentTranslateX.value = centerX - screenCenterX - scaledX
  currentTranslateY.value = centerY - screenCenterY - scaledY

  lastTranslateX.value = currentTranslateX.value
  lastTranslateY.value = currentTranslateY.value
  scale.value = newScale
}

const toggleZoomAt = (clientX: number, clientY: number) => {
  const targetScale = 2.5
  
  if (scale.value < 1.5) {
    // 放大：以点击位置为中心
    const screenCenterX = window.innerWidth / 2
    const screenCenterY = window.innerHeight / 2
    const offsetX = clientX - screenCenterX
    const offsetY = clientY - screenCenterY
    
    // 计算位移让点击位置保持在原位
    currentTranslateX.value = -offsetX * (targetScale - 1)
    currentTranslateY.value = -offsetY * (targetScale - 1)
    lastTranslateX.value = currentTranslateX.value
    lastTranslateY.value = currentTranslateY.value
    scale.value = targetScale
  } else {
    // 缩小：直接归位
    scale.value = 1
    currentTranslateX.value = 0
    currentTranslateY.value = 0
    lastTranslateX.value = 0
    lastTranslateY.value = 0
  }
  
  // 动画结束后解锁
  setTimeout(() => {
    isAnimating.value = false
  }, 280)
}

const handleDoubleTap = (clientX: number, clientY: number) => {
  // 防止动画期间重复触发
  if (isAnimating.value) return false
  
  const now = Date.now()
  const tapLength = now - lastTapTime

  // 检查是否为双击（两次点击位置相近且时间间隔短）
  const dx = clientX - lastTapX
  const dy = clientY - lastTapY
  const distance = Math.sqrt(dx * dx + dy * dy)

  // 增大阈值以适应更多设备
  if (tapLength < 400 && tapLength > 0 && distance < 100) {
    isAnimating.value = true
    toggleZoomAt(clientX, clientY)
    lastTapTime = 0
    return true
  }

  lastTapTime = now
  lastTapX = clientX
  lastTapY = clientY
  return false
}

// 切换信息面板
const toggleInfo = () => {
  showInfo.value = !showInfo.value
}

// 键盘事件
const handleKeydown = (e: KeyboardEvent) => {
  if (!props.visible) return

  switch (e.key) {
    case 'Escape':
      close()
      break
    case 'ArrowLeft':
      prev()
      break
    case 'ArrowRight':
      next()
      break
    case '+':
    case '=':
      zoomIn()
      break
    case '-':
      zoomOut()
      break
  }
}

// 统一的拖拽处理逻辑
const handleDragStart = (clientX: number, clientY: number) => {
  isDragging.value = true
  startX.value = clientX
  startY.value = clientY
}

const handleDragMove = (clientX: number, clientY: number, e: Event) => {
  if (!isDragging.value) return

  // 放大模式下或者正在水平滑动时阻止默认行为
  if (scale.value > 1 || Math.abs(clientX - startX.value) > Math.abs(clientY - startY.value)) {
    e.preventDefault()
  }

  const deltaX = clientX - startX.value
  const deltaY = clientY - startY.value

  if (scale.value > 1) {
    // 放大模式：自由移动
    currentTranslateX.value = lastTranslateX.value + deltaX
    currentTranslateY.value = lastTranslateY.value + deltaY
  } else {
    // 未放大模式：如果看起来像水平滑动，则仅水平移动用于切换
    // 增加一个阈值减少误触
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      let newTranslateX = deltaX
      
      // 边界限制：第一张不能往右滑，最后一张不能往左滑
      if (currentIndex.value === 0 && deltaX > 0) {
        // 第一张图片，添加阻尼效果
        newTranslateX = deltaX * 0.3
      } else if (currentIndex.value === total.value - 1 && deltaX < 0) {
        // 最后一张图片，添加阻尼效果
        newTranslateX = deltaX * 0.3
      }
      
      currentTranslateX.value = newTranslateX
      currentTranslateY.value = 0
    }
  }
}

const handleDragEnd = () => {
  if (!isDragging.value) return
  isDragging.value = false

  if (scale.value > 1) {
    // 放大模式：保存位置
    lastTranslateX.value = currentTranslateX.value
    lastTranslateY.value = currentTranslateY.value
  } else {
    // 未放大模式：判断是否切换图片
    const threshold = 80 // 滑动阈值

    if (currentTranslateX.value > threshold && currentIndex.value > 0) {
      // 往右滑且不是第一张
      prev()
    } else if (currentTranslateX.value < -threshold && currentIndex.value < total.value - 1) {
      // 往左滑且不是最后一张
      next()
    } else {
      // 回弹
      currentTranslateX.value = 0
      currentTranslateY.value = 0
    }
  }
}

// 鼠标事件转换
const onMouseDown = (e: MouseEvent) => {
  // 只有左键点击才触发拖拽
  if (e.button !== 0) return
  handleDragStart(e.clientX, e.clientY)
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const onDblClick = (e: MouseEvent) => {
  // 防止动画期间重复触发
  if (isAnimating.value) return
  
  e.preventDefault()
  e.stopPropagation()
  isAnimating.value = true
  toggleZoomAt(e.clientX, e.clientY)
}

const onMouseMove = (e: MouseEvent) => {
  handleDragMove(e.clientX, e.clientY, e)
}

const onMouseUp = () => {
  handleDragEnd()
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
}

// 触摸事件转换
const onTouchStart = (e: TouchEvent) => {
  if (e.touches.length === 2) {
    // 双指触摸 - 开始捏合缩放
    const touch1 = e.touches[0]
    const touch2 = e.touches[1]
    if (touch1 && touch2) {
      isPinching.value = true
      isDragging.value = false
      initialPinchDistance.value = getDistance(touch1, touch2)
      initialPinchScale.value = scale.value
      e.preventDefault()
    }
  } else if (e.touches.length === 1 && e.touches[0]) {
    // 单指触摸 - 记录起点（不立刻进入拖拽，避免点击误触发）
    const touch = e.touches[0]
    touchStartTime = Date.now()
    touchStartClientX = touch.clientX
    touchStartClientY = touch.clientY
    touchMoved = false
    isDragging.value = false
  }
}

const onTouchMove = (e: TouchEvent) => {
  if (isPinching.value && e.touches.length === 2) {
    // 双指移动 - 捏合缩放
    const touch1 = e.touches[0]
    const touch2 = e.touches[1]
    if (touch1 && touch2) {
      e.preventDefault()
      if (initialPinchDistance.value <= 0) return
      const currentDistance = getDistance(touch1, touch2)
      const scaleRatio = currentDistance / initialPinchDistance.value
      let newScale = initialPinchScale.value * scaleRatio
      
      // 限制缩放范围 (与 zoomAtPoint 保持一致: 0.5-4倍)
      newScale = Math.max(0.5, Math.min(4, newScale))
      
      // 使用动态中心点（跟随手指移动）
      const center = getCenter(touch1, touch2)
      zoomAtPoint(newScale, center.x, center.y, scale.value)
    }
  } else if (!isPinching.value && e.touches.length === 1 && e.touches[0]) {
    // 单指移动 - 超过阈值后进入拖拽
    const touch = e.touches[0]
    const dx = touch.clientX - touchStartClientX
    const dy = touch.clientY - touchStartClientY
    if (!touchMoved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      touchMoved = true
      handleDragStart(touch.clientX, touch.clientY)
    }
    if (isDragging.value) {
      handleDragMove(touch.clientX, touch.clientY, e)
    }
  }
}

const onTouchEnd = (e: TouchEvent) => {
  if (isPinching.value) {
    isPinching.value = false
    // 如果缩放比例接近/小于 1，回弹到 1 并居中
    if (scale.value <= 1.02) {
      scale.value = 1
      currentTranslateX.value = 0
      currentTranslateY.value = 0
      lastTranslateX.value = 0
      lastTranslateY.value = 0
    } else {
      // 保存当前位置
      lastTranslateX.value = currentTranslateX.value
      lastTranslateY.value = currentTranslateY.value
    }
    // 如果还有一个手指在触摸，开始拖拽模式
    if (e.touches.length === 1 && e.touches[0]) {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY)
    }
  } else {
    // 单指抬起：如果不是拖拽，则按 tap 处理，用于双击检测
    const touch = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : null
    const duration = Date.now() - touchStartTime
    if (!touchMoved && duration < 350 && touch) {
      handleDoubleTap(touch.clientX, touch.clientY)
    }
    if (isDragging.value) {
      handleDragEnd()
    }
  }
}

// 背景点击关闭
const handleBackdropClick = (e: MouseEvent) => {
  if ((e.target as HTMLElement).classList.contains('viewer-backdrop')) {
    close()
  }
}

// 浏览器历史状态管理
const handlePopState = () => {
  if (props.visible) {
    close()
  }
}

// 锁定/解锁 body 滚动
const lockBodyScroll = () => {
  document.body.style.overflow = 'hidden'
  document.body.style.touchAction = 'none'
}

const unlockBodyScroll = () => {
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
}

// 监听 visible 变化
watch(() => props.visible, (visible) => {
  if (visible) {
    currentIndex.value = props.initialIndex
    resetViewState()
    lockBodyScroll()
    // 推入历史状态以支持返回键关闭
    history.pushState({ viewer: true }, '')
    window.addEventListener('popstate', handlePopState)
  } else {
    unlockBodyScroll()
    window.removeEventListener('popstate', handlePopState)
  }
})

// 监听 initialIndex 变化
watch(() => props.initialIndex, (newIndex) => {
  if (props.visible) {
    currentIndex.value = newIndex
    resetViewState()
  }
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  if (props.visible) {
    renderBlurHash()
    lockBodyScroll()
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('popstate', handlePopState)
  unlockBodyScroll()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="viewer-fade">
      <div v-if="visible" class="viewer-overlay" @click="handleBackdropClick">
        <!-- 背景遮罩 -->
        <div class="viewer-backdrop"></div>

        <!-- 顶部工具栏 -->
        <header class="viewer-header">
          <div class="header-left">
            <span class="image-counter">{{ currentIndex + 1 }} / {{ total }}</span>
          </div>
          <div class="header-right">
            <button class="viewer-btn" @click="toggleInfo" title="图片信息">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </button>
            <button class="viewer-btn" @click="close" title="关闭">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </header>

        <!-- 图片主体区域 -->
        <div class="viewer-content" @mousedown="onMouseDown" @dblclick="onDblClick" @touchstart="onTouchStart"
          @touchmove="onTouchMove" @touchend="onTouchEnd">
          <!-- 左箭头 -->
          <button v-if="currentIndex > 0" class="nav-btn nav-prev" @click.stop="prev" @mousedown.stop @touchstart.stop @dblclick.stop aria-label="上一张">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <!-- 图片容器 -->
          <div class="image-wrapper" :style="{
            transform: `translate(${currentTranslateX}px, ${currentTranslateY}px) rotate(${rotation}deg) scale(${scale})`,
            transition: (isDragging || isPinching) ? 'none' : 'transform 0.25s ease-out',
            cursor: scale > 1 ? 'grab' : 'default'
          }" :class="{ 'is-grabbing': isDragging && scale > 1 }">
            <!-- BlurHash 占位 -->
            <canvas v-show="!imageLoaded && currentImage?.BlurHash" ref="canvasRef" width="32" height="32"
              class="blur-placeholder"></canvas>

            <!-- 实际图片 -->
            <img v-if="currentImage" :src="getFullImageUrl(currentImage.Key)" :alt="currentImage.Title || ''"
              class="viewer-image" :class="{ 'is-loaded': imageLoaded }" @load="handleImageLoad" draggable="false" />
          </div>

          <!-- 右箭头 -->
          <button v-if="currentIndex < total - 1" class="nav-btn nav-next" @click.stop="next" @mousedown.stop @touchstart.stop @dblclick.stop aria-label="下一张">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <!-- 信息浮层 -->
        <Transition name="slide-up">
          <div v-if="showInfo && currentImage" class="info-panel">
            <div class="info-header">
              <h3>{{ currentImage.Title || '未命名图片' }}</h3>
              <button class="info-close" @click="showInfo = false">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="info-content">
              <p v-if="currentImage.Description" class="info-desc">{{ currentImage.Description }}</p>

              <!-- EXIF 相机信息 -->
              <div v-if="parsedExif" class="exif-info">
                <!-- 设备名称 (制造商 + 型号) -->
                <div v-if="deviceName" class="exif-device">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                  <span>{{ deviceName }}</span>
                </div>

                <!-- 镜头信息 -->
                <div v-if="parsedExif.lens" class="exif-lens">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                  <span>{{ parsedExif.lens }}</span>
                </div>

                <!-- 拍摄参数 -->
                <div class="exif-params"
                  v-if="parsedExif.focalLength || parsedExif.fNumber || parsedExif.exposureTime || parsedExif.iso">
                  <div v-if="parsedExif.focalLength" class="param-item">
                    <span class="param-icon" style="width: 1rem; height: 1rem; display: inline-block;">
                      <svg width="100%" height="100%" viewBox="0 0 50 50" version="1.1"
                        xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                        xml:space="preserve" xmlns:serif="http://www.serif.com/"
                        style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
                        <g transform="matrix(3.125,0,0,3.125,0,0)">
                          <g fill="currentColor" transform="matrix(1,0,0,-1,0,16)">
                            <path
                              d="M1,8C1,11.84 4.16,15 8,15C11.84,15 15,11.84 15,8C15,4.16 11.84,1 8,1C4.16,1 1,4.16 1,8M16,8C16,12.389 12.389,16 8,16C3.611,16 0,12.389 0,8C-0,3.611 3.611,0 8,0C12.389,-0 16,3.611 16,8" />
                          </g>
                          <g fill="currentColor" transform="matrix(0.75,1.84889e-32,1.84889e-32,-0.75,2,14)">
                            <path
                              d="M1,8C1,11.84 4.16,15 8,15C11.84,15 15,11.84 15,8C15,4.16 11.84,1 8,1C4.16,1 1,4.16 1,8M16,8C16,12.389 12.389,16 8,16C3.611,16 0,12.389 0,8C-0,3.611 3.611,0 8,0C12.389,-0 16,3.611 16,8" />
                          </g>
                        </g>
                      </svg>
                    </span>
                    <span>{{ formatFocalLength(parsedExif.focalLength) }}</span>
                  </div>
                  <div v-if="parsedExif.fNumber" class="param-item">
                    <span class="param-icon" style="width: 1rem; height: 1rem; display: inline-block;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                        class="bi bi-circle-half" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 0 8 1zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16" />
                      </svg>
                    </span>
                    <span>{{ formatAperture(parsedExif.fNumber) }}</span>
                  </div>
                  <div v-if="parsedExif.exposureTime" class="param-item">
                    <span class="param-icon" style="width: 1rem; height: 1rem; display: inline-block;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                        class="bi bi-clock" viewBox="0 0 16 16">
                        <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z" />
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0" />
                      </svg>
                    </span>
                    <span>{{ formatExposureTime(parsedExif.exposureTime) }}</span>
                  </div>
                  <div v-if="parsedExif.iso" class="param-item">
                    <span class="param-label">ISO</span>
                    <span>{{ parsedExif.iso }}</span>
                  </div>
                </div>
              </div>

              <!-- 日期信息 -->
              <div class="info-meta">
                <div class="meta-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span>{{ formatDate(currentImage.CreatedAt) }}</span>
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- 底部工具栏 -->
        <footer class="viewer-footer">
          <div class="toolbar">
            <button class="toolbar-btn" @click="zoomOut" title="缩小">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
            <button class="toolbar-btn" @click="resetZoom" title="重置">
              <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
            </button>
            <button class="toolbar-btn" @click="zoomIn" title="放大">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
            <div class="toolbar-divider"></div>
            <button class="toolbar-btn" @click="rotate" title="旋转">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </div>
        </footer>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.viewer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
}

/* 顶部工具栏 */
.viewer-header {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.image-counter {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.viewer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.viewer-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* 图片主体区域 */
.viewer-content {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

.image-wrapper {
  position: relative;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper.is-grabbing {
  cursor: grabbing !important;
}

.blur-placeholder {
  position: absolute;
  width: 100%;
  height: 100%;
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  filter: blur(20px);
  transform: scale(1.1);
}

.viewer-image {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.4s ease;
  user-select: none;
}

.viewer-image.is-loaded {
  opacity: 1;
}

/* 导航按钮 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 80px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 10;
}

.nav-prev {
  left: 0;
  border-radius: 0 8px 8px 0;
}

.nav-next {
  right: 0;
  border-radius: 8px 0 0 8px;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* 信息浮层 */
.info-panel {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 500px;
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  color: white;
  z-index: 20;
  overflow: hidden;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.info-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: white;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.info-close:hover {
  opacity: 1;
}

.info-content {
  padding: 12px 16px;
}

.info-desc {
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.8;
}

/* EXIF 相机信息样式 */
.exif-info {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.exif-device {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 8px;
}

.exif-device svg {
  opacity: 0.7;
}

.exif-lens {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  opacity: 0.7;
  margin-bottom: 10px;
}

.exif-params {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-top: 8px;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.08);
  padding: 4px;
  border-radius: 6px;
}

.param-icon {
  font-size: 12px;
  opacity: 0.6;
}

.param-label {
  font-size: 11px;
  opacity: 0.6;
  margin-right: 2px;
}

.info-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  opacity: 0.7;
}

.meta-label {
  opacity: 0.5;
}

/* 底部工具栏 */
.viewer-footer {
  position: relative;
  z-index: 10;
  padding: 12px 16px 20px;
  display: flex;
  justify-content: center;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 36px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.zoom-level {
  font-size: 13px;
  font-weight: 500;
  min-width: 48px;
  text-align: center;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 4px;
}

/* 动画 */
.viewer-fade-enter-active,
.viewer-fade-leave-active {
  transition: opacity 0.3s ease;
}

.viewer-fade-enter-from,
.viewer-fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateX(-50%) translateY(20px);
  opacity: 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .nav-btn {
    width: 40px;
    height: 60px;
    opacity: 0.6;
  }

  .image-wrapper {
    max-width: 100vw;
    max-height: 75vh;
  }

  .viewer-image,
  .blur-placeholder {
    max-width: 100vw;
    max-height: 75vh;
  }

  .info-panel {
    bottom: 100px;
    width: 95%;
    max-width: none;
    border-radius: 16px;
  }

  .toolbar {
    gap: 2px;
  }

  .toolbar-btn {
    min-width: 36px;
    height: 32px;
  }
}

/* 安全区域适配 (iOS) */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .viewer-footer {
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
}
</style>
